<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  options: { type: Array, default: () => [] },
  selectedOptions: { type: Array, default: null },
  placeholder: { type: String, default: "Please select" },
  disabled: { type: Boolean, default: false },
  dedupeByLabel: { type: Boolean, default: true },
  filterMode: { type: String, default: "startsWith" },
});

const emit = defineEmits(["update:modelValue", "change"]);

const open = ref(false);
const query = ref("");
const hi = ref(-1);

const norm = (opt) =>
  typeof opt === "object" && opt !== null
    ? { label: String(opt.label ?? ""), value: opt.value }
    : { label: String(opt ?? ""), value: opt };

const flat = computed(() => {
  const arr = (props.options || []).map(norm);
  if (!props.dedupeByLabel) return arr;
  const seen = new Set();
  return arr.filter((o) => {
    const key = o.label.trim().toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
});

const selectedValues = computed(() => props.modelValue || []);
const selectedSet = computed(() => new Set(selectedValues.value.map((value) => String(value))));

const selectedOptions = computed(() => {
  if (Array.isArray(props.selectedOptions)) {
    const selectedByValue = new Map(
      props.selectedOptions.map((option) => {
        const normalized = norm(option);
        return [String(normalized.value), normalized];
      })
    );

    return selectedValues.value
      .map((value) => selectedByValue.get(String(value)))
      .filter(Boolean);
  }

  const optionsByValue = new Map(
    flat.value.map((option) => [String(option.value), option])
  );

  return selectedValues.value
    .map((value) => optionsByValue.get(String(value)))
    .filter(Boolean);
});

const normText = (s) => String(s ?? "").trim().toLowerCase();

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase();
  const available = flat.value.filter((option) => !selectedSet.value.has(String(option.value)));
  if (!q) return available;

  return available.filter((option) => {
    const label = option.label.toLowerCase();
    return props.filterMode === "includes" ? label.includes(q) : label.startsWith(q);
  });
});

watch(
  () => props.modelValue,
  () => {
    if (props.disabled) {
      open.value = false;
    }
  },
  { deep: true }
);

function openDd() {
  if (props.disabled) return;
  open.value = true;
  hi.value = -1;
}

function closeDd() {
  open.value = false;
  hi.value = -1;
}

function updateValue(nextValue) {
  emit("update:modelValue", nextValue);
  emit("change", nextValue);
}

function select(option) {
  updateValue([...selectedValues.value, option.value]);
  query.value = "";
  openDd();
}

function remove(value) {
  updateValue(selectedValues.value.filter((item) => String(item) !== String(value)));
}

function clearAll() {
  updateValue([]);
  query.value = "";
  closeDd();
}

function onKey(event) {
  if (props.disabled) return;

  if (event.key === "Backspace" && !query.value && selectedValues.value.length) {
    remove(selectedValues.value[selectedValues.value.length - 1]);
    return;
  }

  if (!open.value && (event.key === "ArrowDown" || event.key === "Enter")) {
    event.preventDefault();
    openDd();
    return;
  }

  if (!open.value) return;

  const max = filtered.value.length - 1;
  if (event.key === "ArrowDown") {
    event.preventDefault();
    hi.value = hi.value < max ? hi.value + 1 : 0;
  } else if (event.key === "ArrowUp") {
    event.preventDefault();
    hi.value = hi.value > 0 ? hi.value - 1 : max;
  } else if (event.key === "Enter") {
    event.preventDefault();
    if (hi.value >= 0) {
      select(filtered.value[hi.value]);
    }
  } else if (event.key === "Escape") {
    event.preventDefault();
    closeDd();
  }
}

function onDocClick(event) {
  if (!event.target.closest?.("[data-mss-root]")) {
    closeDd();
  }
}

onMounted(() => document.addEventListener("click", onDocClick));
onBeforeUnmount(() => document.removeEventListener("click", onDocClick));
</script>

<template>
  <div data-mss-root class="relative">
    <div
      class="relative mt-1 flex min-h-[42px] w-full flex-wrap items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 shadow-sm"
      :class="disabled ? 'bg-gray-50' : 'focus-within:border-indigo-300 focus-within:ring focus-within:ring-indigo-200 focus-within:ring-opacity-50'"
      @click="openDd"
    >
      <span
        v-for="option in selectedOptions"
        :key="`${option.label}__${String(option.value)}`"
        class="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-1 text-sm text-indigo-700"
      >
        <span>{{ option.label }}</span>
        <button
          type="button"
          class="text-indigo-500 hover:text-indigo-700"
          :disabled="disabled"
          @click.stop="remove(option.value)"
        >
          x
        </button>
      </span>

      <input
        type="text"
        v-model="query"
        :placeholder="selectedOptions.length ? '' : placeholder"
        :disabled="disabled"
        autocomplete="off"
        class="min-w-[120px] flex-1 border-0 p-0 focus:outline-none focus:ring-0"
        @focus="openDd"
        @keydown="onKey"
      />

      <span class="pointer-events-none absolute inset-y-0 right-3 flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-600" viewBox="0 0 20 20" fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clip-rule="evenodd"
          />
        </svg>
      </span>
    </div>

    <ul
      v-show="open"
      class="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg"
      role="listbox"
    >
      <template v-if="filtered.length">
        <li
          v-for="(option, idx) in filtered"
          :key="`${option.label}__${String(option.value ?? option.label)}`"
          :class="['cursor-pointer select-none px-3 py-2', idx === hi ? 'bg-indigo-50' : '']"
          role="option"
          @mouseenter="hi = idx"
          @mouseleave="hi = -1"
          @mousedown.prevent="select(option)"
        >
          {{ option.label }}
        </li>
      </template>
      <template v-else>
        <li class="px-3 py-2 text-gray-500">No results</li>
      </template>

      <li
        v-if="selectedOptions.length"
        class="cursor-pointer border-t border-gray-100 px-3 py-2 text-sm text-gray-600"
        @mousedown.prevent="clearAll"
      >
        Clear selection
      </li>
    </ul>
  </div>
</template>
